import type { DeviceModelLedMapping, LedOutput } from '../../services/admin';

// Helpers for the LED Outputs catalog and a model's LED mappings. The server is authoritative
// (it enforces unique color/state and one mapping per output per model); these only give the
// admin immediate, specific feedback.

export function ledOutputLabel(o: Pick<LedOutput, 'color' | 'state'>): string {
  return `${o.color} / ${o.state}`;
}

export function sortLedOutputs<T extends Pick<LedOutput, 'color' | 'state' | 'sort_order'>>(outputs: T[]): T[] {
  return [...outputs].sort(
    (a, b) => a.sort_order - b.sort_order || a.color.localeCompare(b.color) || a.state.localeCompare(b.state)
  );
}

/** One editable row in the model editor's LEDs section. */
export interface LedMappingRow {
  led_output_id: string;
  intent_action: string;
}

export function toLedMappingRows(mappings: DeviceModelLedMapping[] | null | undefined): LedMappingRow[] {
  return (mappings ?? []).map((m) => ({ led_output_id: m.led_output_id, intent_action: m.intent_action }));
}

/**
 * Turns the editor rows into the led_mappings payload. Rows left completely empty are dropped;
 * a half-filled row, an output mapped twice or an output that is no longer in the catalog is an
 * error the admin must fix (nothing is silently discarded).
 */
export function prepareLedMappings(
  rows: LedMappingRow[],
  outputs: Pick<LedOutput, 'id' | 'color' | 'state'>[]
): { mappings: DeviceModelLedMapping[] } | { error: string } {
  const byId = new Map(outputs.map((o) => [o.id, o]));
  const seen = new Set<string>();
  const mappings: DeviceModelLedMapping[] = [];
  for (let i = 0; i < rows.length; i++) {
    const id = rows[i].led_output_id.trim();
    const intent = rows[i].intent_action.trim();
    if (!id && !intent) continue;
    const n = i + 1;
    if (!id) return { error: `LED ${n}: select an LED output.` };
    if (!intent) return { error: `LED ${n}: enter the model's Android intent for ${ledOutputLabel(byId.get(id) ?? { color: '?', state: '?' })}.` };
    const output = byId.get(id);
    if (!output) return { error: `LED ${n}: the selected LED output no longer exists in the catalog.` };
    if (seen.has(id)) return { error: `${ledOutputLabel(output)} is mapped more than once - each LED output can be mapped once per model.` };
    seen.add(id);
    mappings.push({ led_output_id: id, intent_action: intent });
  }
  return { mappings };
}
